import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTheme } from '../contexts/ThemeContext';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, className = '' }) => {
  const { themeClasses, isLightTheme } = useTheme();

  return (
    <div className={`markdown-content ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Headings
          h1: ({ node, ...props }) => (
            <h1 className={`text-3xl font-bold ${themeClasses.text} mt-6 mb-4 pb-2 border-b ${themeClasses.border}`} {...props} />
          ),
          h2: ({ node, ...props }) => (
            <h2 className={`text-2xl font-semibold ${themeClasses.text} mt-5 mb-3`} {...props} />
          ),
          h3: ({ node, ...props }) => (
            <h3 className={`text-xl font-semibold ${themeClasses.text} mt-4 mb-2`} {...props} />
          ),
          h4: ({ node, ...props }) => (
            <h4 className={`text-lg font-semibold ${themeClasses.text} mt-3 mb-2`} {...props} />
          ),
          h5: ({ node, ...props }) => (
            <h5 className={`text-base font-semibold ${themeClasses.text} mt-2 mb-1`} {...props} />
          ),
          h6: ({ node, ...props }) => (
            <h6 className={`text-sm font-semibold ${themeClasses.text} mt-2 mb-1`} {...props} />
          ),
          // Paragraphs
          p: ({ node, ...props }) => (
            <p className={`${themeClasses.text} mb-4 leading-relaxed`} {...props} />
          ),
          // Lists
          ul: ({ node, ...props }) => (
            <ul className={`list-disc list-inside ${themeClasses.text} mb-4 space-y-2 ml-4`} {...props} />
          ),
          ol: ({ node, ...props }) => (
            <ol className={`list-decimal list-inside ${themeClasses.text} mb-4 space-y-2 ml-4`} {...props} />
          ),
          li: ({ node, ...props }) => (
            <li className={`${themeClasses.textSecondary} mb-1`} {...props} />
          ),
          // Links
          a: ({ node, ...props }) => (
            <a
              className={`${isLightTheme ? 'text-cyan-600 hover:text-cyan-700 underline' : 'text-cyan-400 hover:text-cyan-300 underline'}`}
              target="_blank"
              rel="noopener noreferrer"
              {...props}
            />
          ),
          // Code blocks
          code: ({ node, inline, ...props }: any) => {
            if (inline) {
              return (
                <code
                  className={`${isLightTheme ? 'bg-gray-100 text-gray-800' : 'bg-gray-800 text-gray-200'} px-1.5 py-0.5 rounded text-sm font-mono`}
                  {...props}
                />
              );
            }
            return (
              <code
                className={`block ${isLightTheme ? 'bg-gray-100 text-gray-800' : 'bg-gray-800 text-gray-200'} p-4 rounded-lg overflow-x-auto text-sm font-mono mb-4`}
                {...props}
              />
            );
          },
          pre: ({ node, ...props }) => (
            <pre className={`${isLightTheme ? 'bg-gray-100' : 'bg-gray-800'} p-4 rounded-lg overflow-x-auto mb-4`} {...props} />
          ),
          // Blockquotes
          blockquote: ({ node, ...props }) => (
            <blockquote
              className={`border-l-4 ${isLightTheme ? 'border-cyan-300 bg-cyan-50' : 'border-cyan-600 bg-cyan-900/20'} pl-4 py-2 my-4 italic ${themeClasses.textSecondary}`}
              {...props}
            />
          ),
          // Horizontal rules
          hr: ({ node, ...props }) => (
            <hr className={`${themeClasses.border} my-6`} {...props} />
          ),
          // Tables
          table: ({ node, ...props }) => (
            <div className="overflow-x-auto my-4">
              <table className={`min-w-full border-collapse border ${themeClasses.border}`} {...props} />
            </div>
          ),
          thead: ({ node, ...props }) => (
            <thead className={`${isLightTheme ? 'bg-gray-100' : 'bg-gray-800'}`} {...props} />
          ),
          tbody: ({ node, ...props }) => (
            <tbody {...props} />
          ),
          tr: ({ node, ...props }) => (
            <tr className={`border-b ${themeClasses.border}`} {...props} />
          ),
          th: ({ node, ...props }) => (
            <th className={`border ${themeClasses.border} px-4 py-2 text-left font-semibold ${themeClasses.text}`} {...props} />
          ),
          td: ({ node, ...props }) => (
            <td className={`border ${themeClasses.border} px-4 py-2 ${themeClasses.textSecondary}`} {...props} />
          ),
          // Strong and emphasis
          strong: ({ node, ...props }) => (
            <strong className={`font-bold ${themeClasses.text}`} {...props} />
          ),
          em: ({ node, ...props }) => (
            <em className={`italic ${themeClasses.text}`} {...props} />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;


